import { logger } from "@mwt/web-js-telemetry";
import { useEffect, useRef } from "react";

import { PerformanceDataBagColumns } from "..";

interface LargestContentfulPaint extends PerformanceEntry {
  /**
   * The element that is the current largest contentful paint.
   */
  element?: HTMLElement;
  /**
   * The time the element was rendered to the screen.
   * May not be available if the element is a cross-origin image
   * loaded without the Timing-Allow-Origin header.
   */
  renderTime?: number;
  // The id of the element. This property returns an empty string when there is no id.
  id?: string;
}

// IE 11. Reference: https://developer.mozilla.org/en-US/docs/Web/API/PerformanceNavigation/type
const entryType = {
  0: "TYPE_NAVIGATE",
  1: "TYPE_RELOAD",
  2: "TYPE_BACK_FORWARD",
  255: "TYPE_RESERVED",
};

export const usePageLoadTelemetryTracker = (eventType?: string): void => {
  const componentFirstRender = useRef<boolean>(true);

  useEffect(() => {
    if (componentFirstRender.current) {
      if (window.performance) {
        const performance = window.performance;
        const fcpEntry = performance?.getEntriesByName(
          "first-contentful-paint"
        )?.[0];
        const fpEntry = performance?.getEntriesByName("first-paint")?.[0];

        // Get Navigation Timing entry.
        const navigationEntry = performance?.getEntriesByType(
          "navigation"
        )?.[0] as unknown as PerformanceNavigationTiming;

        const navigationType =
          typeof navigationEntry?.type === "number"
            ? entryType[navigationEntry?.type]
            : navigationEntry?.type;

        /**
         * A number that is the size (in octets) received from the fetch (HTTP or cache)
         * of the message body, after removing any applied content-codings.
         */
        const contentLength = navigationEntry?.decodedBodySize ?? 0;

        /**
         * Time it took to render the page.
         * This is obtained by starting with the time at which loading of
         * the DOM and its dependencies is complete (domComplete)
         */
        const renderTime = navigationEntry?.domComplete;

        /**
         * Returns a timestamp that is the difference between the
         * `PerformanceNavigationTiming.loadEventEnd`
         * and PerformanceEntry.startTime properties.
         */
        const duration = navigationEntry?.duration;

        // Returns the timestamp when the first contentful paint occurred.
        const fcp = fcpEntry?.startTime;
        // Returns the timestamp when the first paint occurred.
        const fp = fpEntry?.startTime;
        const hostname = window.location.hostname;
        const uri = window.location.href;
        const name = window.location.pathname;
        const refUri = document.referrer;

        try {
          new PerformanceObserver((entryList, observer) => {
            const lcpEntry = entryList?.getEntriesByType(
              "largest-contentful-paint"
            )?.[0] as LargestContentfulPaint;

            logger.logPageView(
              {
                name,
                uri,
                refUri,
                contentLength,
                hostname,
                duration,
                fcp,
                databag: {
                  [PerformanceDataBagColumns.DataBagFP]: fp,
                  [PerformanceDataBagColumns.DataBagRenderTime]: renderTime,
                  [PerformanceDataBagColumns.DataBagNavigationType]:
                    navigationType,
                  [PerformanceDataBagColumns.DataBagLCPStartTime]:
                    lcpEntry?.startTime,
                  [PerformanceDataBagColumns.DataBagElement]:
                    lcpEntry?.element?.className ?? "",
                  [PerformanceDataBagColumns.DataBagLCPRenderTime]:
                    lcpEntry?.renderTime ?? "",
                },
              },
              eventType
            );

            // Stop observing on receiving the entryList once.
            observer.disconnect();
          }).observe({ type: "largest-contentful-paint", buffered: true });
        } catch (_) {
          // IE 11
          logger.logPageView(
            {
              name,
              uri,
              refUri,
              contentLength,
              hostname,
              duration,
              fcp,
              databag: {
                [PerformanceDataBagColumns.DataBagFP]: fp,
                [PerformanceDataBagColumns.DataBagRenderTime]: renderTime,
                [PerformanceDataBagColumns.DataBagNavigationType]:
                  navigationType,
              },
            },
            eventType
          );
        }
      } else {
        //no-op
      }
      componentFirstRender.current = false;
    }
  }, [eventType]);
};
